import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "../../../../components/modals/BaseModal";
import { ComunicacionAjax } from "../../../../ts/api/comunicacion";
import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { TypedTable } from "../../../../components/TypedTable";
import { notificacionError, notificacion } from "../../../../components/Alerts";
import type { Respuesta } from "../../../../ts/typings/clases";
import { formatIDArt } from "../../../../ts/util";
import { useDisclosureData } from "../../../../hooks/UseDisclosure";
import { Loading, QueryError } from "../../../../components/Loading/Loading";

interface ModalEditarUbicacionProps extends BaseModalProps {
	readonly idUbicacion: string | null;
}
export function ModalEditarUbicacion(props: ModalEditarUbicacionProps) {
	const queryClient = useQueryClient();


	const eliminarUbicacion = (id: string) => {
		if (!confirm("¿Está seguro de que desea eliminar la ubicación?")) {
			console.debug("Peticion cancelada por el usuario");
			return;
		}

		const parametro = {
			"id": id,
		};

		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/eliminarUbicacion", parametro)
			.then(respuesta => {
				if (respuesta.tipo == "error") {
					notificacionError(respuesta.message ?? "Error al eliminar la ubicación.");
					return;
				}

				notificacion(respuesta.message ?? "Ubicación eliminada con éxito.");
				void queryClient.invalidateQueries({ queryKey: ["getEstanterias"] });
				void queryClient.invalidateQueries({ queryKey: ["getEstanteria", data?.idAlmacen, data?.idFila] });


				props.close();
			});
	};



	const { data, error, isPending } = UseDatosUbicacion(props.idUbicacion);

	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Ubicación {props.idUbicacion}</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body" style={{ minWidth: "75vw" }}>
				{isPending == true && <Loading />}

				{error != null && (
					<QueryError error={error} fallback="Error al cargar los datos de la ubicación" />
				)}

				{data != null && props.idUbicacion != null && (
					<CuerpoModal idUbicacion={props.idUbicacion} data={data} />
				)}
			</div>

			<div className="modal-footer">
				<p style={{ color: "red" }} >
					{(data?.articulosPresentes.length ?? 0) > 0 ? "No se puede eliminar la ubicación porque tiene artículos asignados" : ""}
				</p>
				<button type="button" className="btnSecondary" onClick={() => eliminarUbicacion(props.idUbicacion ?? "")} disabled={(data?.articulosPresentes.length ?? 1) > 0}>Eliminar</button>
				<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
			</div>
		</BaseModal>
	);
}

function CuerpoModal(props: { readonly idUbicacion: string; readonly data: DatosUbicacion; }) {
	const editarArtFijo = useDisclosureData<ArticuloFijo | null>(null);

	return (
		<div style={{ display: "flex", gap: 20 }}>
			<div style={{ flex: 2 }}>
				<h1 style={{ margin: 0 }}>Artículos fijos</h1>

				<TablaFijos
					data={props.data.articulosFijos}
					openEditar={editarArtFijo.open}
				/>
				{editarArtFijo.isOpen ? (
					<FormArticuloFijo
						articulo={editarArtFijo.data}
						close={editarArtFijo.close}
						idUbicacion={props.idUbicacion}
					/>
				) : (
					<div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
						<button type="button" className="btnPrimary" onClick={() => editarArtFijo.open(null)}>
							Nuevo artículo
						</button>
					</div>
				)}

			</div>

			<div style={{ flex: 3 }}>
				<h1 style={{ margin: 0 }}>Artículos en la ubicación</h1>
				<TablaArticulo data={props.data.articulosPresentes} />
			</div>



		</div>
	);
}

function TablaArticulo(props: { readonly data: ArticuloUbi[]; }) {
	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<ArticuloUbi>();
		const articuloFaseAccesor = (info: ArticuloUbi) => formatIDArt(info.xarticulo_id, info.xfase_id);

		return [
			columnHelper.accessor(articuloFaseAccesor, { header: "Artículo", meta: { className: "dt-center" } }),
			columnHelper.accessor("xdescripcion", { header: "Descripción", meta: { className: "dt-center" } }),
			columnHelper.accessor("xcontenedor_id", { header: "Contenedor", meta: { className: "dt-center" }, }),
			columnHelper.accessor("xcantidad", { header: "Cantidad", meta: { className: "dt-center" }, }),
		];
	}, []);


	return <TypedTable
		className="dataTable display"
		data={props.data}
		columns={columns}
	/>;
}
function TablaFijos(props: { readonly data: ArticuloFijo[]; readonly openEditar: (art: ArticuloFijo) => void; }) {
	const { openEditar } = props;
	const queryClient = useQueryClient();

	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<ArticuloFijo>();


		const eliminarArticuloFijo = (id: number) => {
			void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/eliminarArticuloFijo", { id }).then((respuesta) => {
				if (respuesta.tipo == "error") {
					notificacionError(respuesta.message ?? "Error al eliminar el artículo fijo.");
					return;
				}

				notificacion("Articulo eliminado");
				void queryClient.invalidateQueries({ queryKey: ["getDatosUbicacion", id] });
			});
		};

		const getAccionArticuloFijo = (linea: ArticuloFijo) => {
			return (
				<div className="clickable" style={{ display: "flex", justifyContent: "space-around" }}>
					<a target="_self" className="main-color btnIcon" onClick={() => openEditar(linea)}>
						<i className="fa fa-pen-to-square" style={{ fontSize: 25, margin: "0 10px" }} />
					</a>
					<a target="_self" className="main-color btnIcon" onClick={() => eliminarArticuloFijo(linea.id)}>
						<i className="fa fa-trash" style={{ fontSize: 25, margin: "0 10px" }} />
					</a>
				</div>
			);
		};


		return [
			columnHelper.accessor("idArticulo", { header: "Artículo", meta: { className: "dt-center" } }),
			columnHelper.accessor("minimo", { header: "Mínimo", meta: { className: "dt-center" } }),
			columnHelper.accessor("maximo", { header: "Máximo", meta: { className: "dt-center" } }),
			columnHelper.display({ id: "acciones", header: "", meta: { className: "dt-center" }, cell: (info) => getAccionArticuloFijo(info.row.original) }),
		];
	}, [openEditar, queryClient]);

	return <TypedTable
		className="dataTable display"
		data={props.data}
		columns={columns}
		noDataMessage="No hay artículos fijos asignados"
	/>;
}

interface FormArticuloFijoProps {
	readonly close: () => void;
	readonly idUbicacion: string;
	readonly articulo: ArticuloFijo | null;
}
function FormArticuloFijo(props: FormArticuloFijoProps) {
	const queryClient = useQueryClient();

	const guardarArticuloFijo = (e: React.FormEvent) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);

		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/modificarArticuloFijo", {
			xarticulo_id: formData.get("idArticuloFijo"),
			ubicacion_id: props.idUbicacion,
			minimo: formData.get("minimoFijo"),
			maximo: formData.get("maximoFijo"),
		})
			.then((response) => {
				if (response.tipo == "error") {
					notificacionError(response.message ?? "Error al guardar el artículo fijo.");
					return;
				}

				void queryClient.invalidateQueries({ queryKey: ["getDatosUbicacion", props.idUbicacion] });
				props.close();
			});
	};

	return (
		<form onSubmit={guardarArticuloFijo} key={props.articulo?.id ?? "new"}>
			<div style={{ display: "grid", gridAutoFlow: "column", gap: 10, marginTop: 10 }} >
				<div>
					<input type="text" className="inputsForm" name="idArticuloFijo" defaultValue={props.articulo?.idArticulo ?? ""} placeholder="Artículo" />
				</div>

				<div>
					<input type="number" className="inputsForm" name="minimoFijo" defaultValue={props.articulo?.minimo ?? ""} placeholder="Mínimo" />
				</div>

				<div>
					<input type="number" className="inputsForm" name="maximoFijo" defaultValue={props.articulo?.maximo ?? ""} placeholder="Máximo" />
				</div>

				<div style={{ display: "flex", gap: "5px" }} >
					<button type="submit" className="btnIcon primary">
						<i className="fa fa-floppy-disk" style={{ fontSize: 25 }} />
					</button>
					<button type="button" className="btnIcon" onClick={props.close}>
						<i className="fa fa-xmark" style={{ fontSize: 25 }} />
					</button>
				</div>

			</div>

		</form>
	);
}


function UseDatosUbicacion(id: string | null) {
	return useQuery<DatosUbicacion | null>({
		queryKey: ["getDatosUbicacion", id],
		queryFn: () => {
			if (id == null) return Promise.resolve(null);
			return ComunicacionAjax.sendAjaxRequest<DatosUbicacion>("GET", "/api/v1/getDatosUbicacion", { "id": id, });
		}
	});
}
interface DatosUbicacion {
	id: number,
	idUbicacion: string;
	idAlmacen: string;
	idFila: string;
	idColumna: string;
	idAltura: string;
	articulosFijos: ArticuloFijo[],
	articulosPresentes: ArticuloUbi[];
}
interface ArticuloFijo {
	id: number,
	idUbicacion: number,	// Este es el ID de la FILA, no lo que solemos llamar id ubicacion
	idArticulo: string,
	maximo: number,
	minimo: number,
}
interface ArticuloUbi {
	xarticulo_id: string,
	xfase_id: number,
	xcantidad: number,
	xdescripcion: string,
	xcontenedor_id: number | null;
}
